import type { Question } from '../types';

// ---------------------------------------------------------------------------
// Coverage Selection Questions
// ---------------------------------------------------------------------------

export const coverageSelectQuestions: Question[] = [
  {
    id: 'coverage_lines',
    text: 'Which lines of coverage do you need?',
    subtext: 'Select all that apply. You can always add more later.',
    section: 'coverage',
    sort_order: 10,
    field_type: 'multiselect',
    options: [
      {
        label: 'General Liability / BOP',
        value: 'gl_bop',
        help: 'Covers third-party bodily injury, property damage, and business property.',
      },
      {
        label: 'Workers Compensation',
        value: 'workers_comp',
        help: 'Required in most states for businesses with employees.',
      },
      {
        label: 'Commercial Auto',
        value: 'commercial_auto',
        help: 'Covers vehicles used for business purposes.',
      },
      {
        label: 'Professional / E&O / Cyber',
        value: 'professional_eo_cyber',
        help: 'Covers professional mistakes, data breaches, and cyber liability.',
      },
      {
        label: 'Directors & Officers',
        value: 'dno',
        help: 'Protects company leadership from personal liability.',
      },
      {
        label: 'Employment Practices Liability',
        value: 'epli',
        help: 'Covers claims of wrongful termination, discrimination, harassment.',
      },
      {
        label: 'Umbrella / Excess',
        value: 'umbrella',
        help: 'Additional liability limits above underlying policies.',
      },
      {
        label: 'Commercial Property',
        value: 'property',
        help: 'Covers buildings, contents, and loss of rental/business income.',
      },
      {
        label: 'Equipment Breakdown',
        value: 'equipment_breakdown',
        help: 'Covers HVAC, boilers, generators, elevators, and other mechanical/electrical equipment.',
      },
      {
        label: 'Environmental / Pollution',
        value: 'environmental',
        help: 'Covers pollution events — tanks, septic systems, mold, and similar exposures.',
      },
      {
        label: 'Cyber & Crime',
        value: 'cyber_crime',
        help: 'Covers data breaches, ransomware, social engineering, and employee dishonesty.',
      },
      {
        label: 'Builders Risk',
        value: 'builders_risk',
        help: 'Course-of-construction coverage for expansion, new construction, or major renovation.',
      },
    ],
    required: true,
    field_path: 'coverage.lines_needed',
    conditions: [],
    tags: ['coverage', 'core'],
  },

  {
    id: 'coverage_effective_date',
    text: 'When do you need coverage to begin?',
    subtext: 'The desired policy effective date.',
    section: 'coverage',
    sort_order: 20,
    field_type: 'date',
    required: true,
    field_path: 'coverage.effective_date',
    conditions: [],
    acord_field: 'ProposedEffDt',
    tags: ['coverage', 'core'],
  },

  {
    id: 'coverage_current_carrier',
    text: 'Who is your current insurance carrier?',
    subtext: 'Enter "None" if this is a new policy.',
    section: 'coverage',
    sort_order: 30,
    field_type: 'text',
    placeholder: 'Hartford, Travelers, None...',
    required: false,
    field_path: 'coverage.current_carrier',
    conditions: [],
    acord_field: 'CurrentCarrier',
    tags: ['coverage'],
  },

  {
    id: 'coverage_expiration_date',
    text: 'When does your current policy expire?',
    section: 'coverage',
    sort_order: 40,
    field_type: 'date',
    required: false,
    field_path: 'coverage.current_expiration',
    conditions: [{ field: 'coverage_current_carrier', operator: 'exists' }],
    acord_field: 'CurrentExpDt',
    tags: ['coverage'],
  },

  {
    id: 'coverage_claims_5yr',
    text: 'Have you had any insurance claims in the past 5 years?',
    section: 'coverage',
    sort_order: 50,
    field_type: 'boolean',
    required: true,
    field_path: 'coverage.claims_past_5yr',
    conditions: [],
    tags: ['coverage', 'claims', 'core'],
  },

  {
    id: 'coverage_claims_description',
    text: 'Please describe the claims',
    subtext: 'Include approximate dates, types of claims, and amounts paid or reserved.',
    section: 'coverage',
    sort_order: 60,
    field_type: 'textarea',
    placeholder:
      '2023 — WC claim, employee back injury, $12,000 paid. 2024 — GL slip-and-fall at jobsite, $5,000 reserved.',
    required: true,
    field_path: 'coverage.claims_description',
    conditions: [{ field: 'coverage_claims_5yr', operator: 'eq', value: true }],
    tags: ['coverage', 'claims'],
  },
];
